import { Form, Formik } from 'formik';
import {
  Checkbox,
  Grid,
  GridColumn,
  GridRow,
  MailIcon,
  MobilePhoneIcon,
  NotificationDefaultIcon,
} from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { ModalBottomButtons } from '../ModalBottomButtons/ModalBottomButtons';
import styles from './NotificationSettingsForm.module.scss';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { showErrorToast } from '../../utils/showErrorToast';
import { CombinedError, useMutation } from 'urql';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { NotificationOptions, NotificationTypes } from './types';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { ProfileNotificationPreferencesConnectFieldInput } from 'client/gql/graphql';

const CreateNotificationPreferencesMutationDocument = graphql(`
  mutation CreateNotificationPreferences($input: [PreferenceCreateInput!]!) {
    createPreferences(input: $input) {
      info {
        nodesCreated
      }
    }
  }
`);
const UpdateProfileMutationDocument = graphql(`
  mutation UpdateProfile($uuid: ID!, $update: ProfileUpdateInput!) {
    updateProfiles(where: { uuid: $uuid }, update: $update) {
      profiles {
        __typename
        prefixName
        displayName
        selectName
        displayNameShort
        organizationRoles
        firstName
        firstNames
        lastName
        uuid
        title
        salutation
        userStatus
        organization {
          uuid
        }
      }
    }
  }
`);

const NotificationSettingsFormFieldsDocument = graphql(`
  fragment NotificationSettingsFormFields on Query {
    ...PermissionCheckerFields
    profiles(where: $profilesWhere, options: $profilesOptions) {
      uuid
      __typename
      notificationPreferencesConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            name
          }
          properties {
            __typename
            transports
          }
        }
      }
    }
  }
`);

type NotificationSettingsFormProps = {
  query: FragmentType<typeof NotificationSettingsFormFieldsDocument> | undefined;
  onClose: () => void;
};
export const NotificationSettingsForm = ({ query, onClose }: NotificationSettingsFormProps) => {
  const { t } = useTranslation();
  const pimAuthClaims = useAuthClaims().pimAuthClaims;

  const fragment = getFragmentData(NotificationSettingsFormFieldsDocument, query);
  const data =
    fragment?.profiles[0]?.notificationPreferencesConnection.edges.map((e) => ({
      name: e.node.name ?? '',
      value: e.properties.transports as NotificationTypes[],
    })) ?? [];

  const perms = usePermissionChecker(fragment);

  const [, createPreferences] = useMutation(CreateNotificationPreferencesMutationDocument);

  function getServices(name: NotificationOptions): NotificationTypes[] {
    return data?.find((n) => n.name === name)?.value ?? ['bell', 'mail'];
  }

  const initialValues = {
    assignmentOpened: getServices(NotificationOptions.AssignmentOpened),
    assignmentUpdated: getServices(NotificationOptions.AssignmentUpdated),
    assignmentDeleted: getServices(NotificationOptions.AssignmentDeleted),
    submissionNew: getServices(NotificationOptions.SubmissionNew),
    submissionUpdated: getServices(NotificationOptions.SubmissionUpdated),
    newMaterial: getServices(NotificationOptions.NewMaterial),
    materialUpdated: getServices(NotificationOptions.MaterialUpdated),
    materialDeleted: getServices(NotificationOptions.MaterialDeleted),
    newMessage: getServices(NotificationOptions.NewMessage),
    newAnnouncement: getServices(NotificationOptions.NewAnnouncement),
    newGroupMembership: getServices(NotificationOptions.NewGroupMembership),
    newCourseMembership: getServices(NotificationOptions.NewCourseMembership),
    conferenceStarted: getServices(NotificationOptions.ConferenceStarted),
    newAppointment: getServices(NotificationOptions.NewAppointment),
    newCollaboratingCourseOrGroup: getServices(NotificationOptions.NewCollaboratingCourseOrGroup),
  };

  const checkboxes = [
    {
      group: t('assignments.title'),
      boxes: [
        { channel: NotificationOptions.AssignmentOpened, label: t('notificationSettings.newAssignment') },
        { channel: NotificationOptions.AssignmentUpdated, label: t('notificationSettings.assignmentUpdated') },
        { channel: NotificationOptions.AssignmentDeleted, label: t('notificationSettings.assignmentDeleted') },
        { channel: NotificationOptions.SubmissionNew, label: t('notificationSettings.newSubmission') },
        { channel: NotificationOptions.SubmissionUpdated, label: t('notificationSettings.submissionUpdated') },
      ],
    },
    {
      group: t('workmaterials.title'),
      boxes: [
        { channel: NotificationOptions.NewMaterial, label: t('notificationSettings.newMaterial') },
        { channel: NotificationOptions.MaterialUpdated, label: t('notificationSettings.materialUpdated') },
        { channel: NotificationOptions.MaterialDeleted, label: t('notificationSettings.materialDeleted') },
      ],
    },
    {
      group: t('messages.title'),
      boxes: [
        { channel: NotificationOptions.NewMessage, label: t('notificationSettings.newMessage') },
        { channel: NotificationOptions.NewAnnouncement, label: t('notificationSettings.newAnnouncement') },
      ],
    },
    {
      group: t('common.miscellaneous'),
      boxes: [
        { channel: NotificationOptions.NewCourseMembership, label: t('notificationSettings.newCourseMembership') },
        { channel: NotificationOptions.NewGroupMembership, label: t('notificationSettings.newGroupMembership') },
        { channel: NotificationOptions.ConferenceStarted, label: t('notificationSettings.conferenceStarted') },
        { channel: NotificationOptions.NewAppointment, label: t('notificationSettings.newAppointment') },
        {
          channel: NotificationOptions.NewCollaboratingCourseOrGroup,
          label: t('notificationSettings.collabCourseOrGroupCreated'),
        },
      ],
    },
  ];

  const [, updateProfile] = useMutation(UpdateProfileMutationDocument);

  const onSubmit = async (values: typeof initialValues) => {
    if (perms?.canUpdateProfile(pimAuthClaims.getProfile())) {
      const existingPreferences = data?.map((p) => p.name);
      const missingPreferences = Object.keys(values).filter((name) => !existingPreferences?.includes(name));
      if (missingPreferences.length > 0) {
        await createPreferences({ input: missingPreferences.map((name) => ({ name })) });
      }

      const payload: ProfileNotificationPreferencesConnectFieldInput[] = Object.entries(values).map(
        ([name, value]) => ({
          overwrite: true,
          where: { node: { name } },
          edge: { transports: value },
        }),
      );
      const resp = await updateProfile({
        uuid: pimAuthClaims.getProfile().uuid,
        update: {
          notificationPreferences: [{ connect: payload }],
        },
      });
      if (resp.error) {
        showErrorToast(resp.error);
      } else {
        showSuccessToast(t('common.saved'));
        onClose();
      }
    }

    if (!perms?.canUpdateProfile(pimAuthClaims.getProfile())) {
      showErrorToast({ message: t('common.notAllowed') } as CombinedError);
      onClose();
    }
  };

  return (
    <Formik<typeof initialValues> initialValues={initialValues} onSubmit={onSubmit}>
      {({ resetForm, setFieldValue, values, isSubmitting, errors }) => {
        const closeForm = () => {
          resetForm();
          onClose();
        };

        const handleClick = async (key: keyof typeof initialValues, channel: NotificationTypes) => {
          const newValue = values[key];
          const index = newValue.indexOf(channel);
          if (index !== -1) {
            newValue.splice(index, 1);
          } else {
            newValue.push(channel);
          }
          await setFieldValue(key, newValue);
        };

        const handleSelect = async (select: boolean) => {
          for (const key in initialValues) {
            let value = values[key as keyof typeof values] as Array<NotificationTypes>;
            value = [];
            if (select) value = ['bell', 'push', 'mail'];
            await setFieldValue(key, value);
          }
        };

        return (
          <Form>
            <Grid className={styles['notifications-settings-form']}>
              <GridRow mobileGap='var(--grid-column-gap)' className={styles['grid-row']}>
                {checkboxes.map((c, i) => (
                  <GridColumn key={i} width={3}>
                    <div className={styles.header}>
                      <NotificationDefaultIcon />
                      <MobilePhoneIcon />
                      <MailIcon />
                      <span className={styles.group}>{c.group}</span>
                    </div>
                    {c.boxes.map((b, i) => {
                      return (
                        <div key={i} className={styles.boxes}>
                          <Checkbox
                            className={styles.bell}
                            checked={values[b.channel]?.includes('bell')}
                            name={`${b.channel}_bell`}
                            onChange={() => handleClick(b.channel, 'bell')}
                          />
                          <Checkbox
                            disabled
                            checked={false}
                            // checked={values[b.channel]?.includes('push')}
                            className={styles.push}
                            name={`${b.channel}_push`}
                            onChange={() => handleClick(b.channel, 'push')}
                          />
                          <Checkbox
                            className={styles.mail}
                            checked={values[b.channel]?.includes('mail')}
                            name={`${b.channel}_mail`}
                            onChange={() => handleClick(b.channel, 'mail')}
                          />
                          <div className={styles.label}>{b.label}</div>
                        </div>
                      );
                    })}
                  </GridColumn>
                ))}
              </GridRow>
            </Grid>
            <ModalBottomButtons
              errors={errors}
              closeButton={{ callback: closeForm }}
              isLoading={isSubmitting}
              additionalButtons={[
                {
                  text: t('notifications.selectAll'),
                  hierarchy: 'tertiary',
                  callback: () => handleSelect(true),
                },
                {
                  text: t('notifications.deselectAll'),
                  hierarchy: 'tertiary',
                  callback: () => handleSelect(false),
                },
              ]}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
